import { useEffect, useState } from 'react';
import {
  loadDashboardAssessmentData,
  type DashboardAssessmentData,
} from '../utils/dashboardAssessmentAdapter';
import { clientCacheKey, peekClientDataCache } from '../utils/clientProjectDataCache';

export function useDashboardAssessmentData(
  userId: number | null | undefined,
  projectId: string | number | null | undefined
) {
  const [data, setData] = useState<DashboardAssessmentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || projectId == null || projectId === '') {
      setData(null);
      return;
    }

    let cancelled = false;
    const cacheKey = clientCacheKey(['dash_assess', userId, projectId]);
    const cached = peekClientDataCache<DashboardAssessmentData>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
    }

    const fetchData = async () => {
      if (!cached) {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await loadDashboardAssessmentData(userId, Number(projectId));
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled && !cached) {
          setError(err instanceof Error ? err.message : 'Failed to load assessment data');
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [userId, projectId]);

  return { data, loading, error };
}
